import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Icon } from '@/ui/common';

export interface LiveInlineSearchProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Client-only Live feed search — filters already-loaded rows by home / away /
 * tournament name (no network request).
 */
export function LiveInlineSearch({ value, onChange, className }: LiveInlineSearchProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const label = t('Search', { defaultValue: 'Search' });

  return (
    <div className={cn('min-w-0 flex-1 lg:w-72 lg:flex-none', className)}>
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <div
        className={cn(
          'flex h-8 w-full items-center gap-2 rounded-md border border-border-default bg-bg-surface px-2.5',
          'focus-within:border-border-focus',
        )}
      >
        <Icon name="search" size={14} className="shrink-0 text-icon-muted" />
        <input
          id={inputId}
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={label}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-body-l text-text-primary outline-none',
            'placeholder:text-text-secondary',
          )}
        />
      </div>
    </div>
  );
}
